import Dashboard from "components/Dashboard";
import Loader from "components/generic/Loader";
import { MostCommonAnswers, DimensionsGraph } from "components/Graphs";
import Recommendations from "components/Recommendations";
import ReportControls, {
  BreadCrumbs,
  ClientTasks,
  Legend,
  NavigationBar,
} from "components/ReportControls";
import useBreakpoints from "hooks/common/useBreakpoints";
import useReport from "hooks/useReport";
import { useEffect } from "react";
import { useErrorHandler } from "react-error-boundary";
import { Dimensions } from "types/dimensions";
import testingData from "../data/testing-data";

function ClientDashboardTesting() {
  const { state, clientHandlers, error, genericHandlers } = useReport({
    passedInitialState: testingData,
  });
  useErrorHandler(error);
  const breakpoints = useBreakpoints({ active: "md" });

  useEffect(() => {
    if (breakpoints.active === "sm" && !state.recommendationsIsActive) {
      clientHandlers.toggleRecommendations();
    }
  }, [breakpoints.active, state.recommendationsIsActive, clientHandlers]);

  useEffect(() => {
    // Clean up and wipe state on dismount to prevent any memory leaks
    return () => genericHandlers.wipeState();
  }, []);

  return (
    <Dashboard>
      <ReportControls breakpoints={breakpoints}>
        <BreadCrumbs
          isLoading={state.activity["dashboard-state"] === "isLoading"}
          crumbs={state.crumbs}
        />
        <ClientTasks
          activeTab={state.activeTab}
          currentDrilldown={state.currentDrilldown}
          benchmarkIsActive={state.benchmarkIsActive}
          benchmarkAvailable={state.benchmarkAvailable}
          benchmarks={state.benchmarkData}
          isLoading={state.activity["dashboard-state"] === "isLoading"}
          clientScores={state.clientScores}
          toggleBenchmark={genericHandlers.toggleBenchmark}
          toggleRecommendations={clientHandlers.toggleRecommendations}
        />
        <NavigationBar
          isLoading={state.activity["dashboard-state"] === "isLoading"}
          activeTab={state.activeTab}
          setActiveTab={genericHandlers.setActiveTab}
          toggleDrilldown={genericHandlers.toggleDrilldown}
          currentDrilldown={state.currentDrilldown}
          setDrilldown={genericHandlers.setDrilldown}
          drilldownIsActive={state.drilldownIsActive}
        />
        <Legend
          isLoading={state.activity["dashboard-state"] === "isLoading"}
          list={state.legend}
        />
      </ReportControls>
      <Loader
        isLoading={state.activity["dashboard-state"] === "isLoading"}
        loadingText="Loading your report..."
      >
        {state.currentDrilldown === undefined ? (
          <DimensionsGraph
            scores={state.clientScores}
            benchmarks={state.benchmarkData}
            activeTab={state.activeTab}
            benchmarkActive={state.benchmarkIsActive}
            surveyCodes={state.surveyCodes}
            setLegend={genericHandlers.setLegend}
          />
        ) : (
          <MostCommonAnswers
            setLegend={genericHandlers.setLegend}
            activeTab={state.activeTab as Dimensions}
            currentDrilldown={state.currentDrilldown}
            answerData={state.drilldownData}
          />
        )}
        <Recommendations
          benchmarkAvailable={state.benchmarkAvailable}
          benchmarks={state.benchmarkData}
          isLoading={state.activity["dashboard-state"] === "isLoading"}
          clientScores={state.clientScores}
          handleToggle={clientHandlers.toggleRecommendations}
          isActive={state.recommendationsIsActive}
          breakpoints={breakpoints}
        />
      </Loader>
    </Dashboard>
  );
}

export default ClientDashboardTesting;
